import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ProfileService, UserPromocodeService } from '@nebalkon-workspace/auth';
import { CABINET_PATHS, WalletService } from '@nebalkon-workspace/cabinet-core';
import {
  resolveCabinetProfilePrerequisites,
  resolveIncompleteProfileBannerParams,
  resolvePrerequisiteLabelKey,
} from './cabinet-profile-prerequisites.util';

@Component({
  selector: 'lib-cabinet-profile-incomplete-banner',
  imports: [RouterLink, TranslatePipe],
  templateUrl: './cabinet-profile-incomplete-banner.component.html',
  styleUrl: './cabinet-profile-incomplete-banner.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CabinetProfileIncompleteBannerComponent {
  readonly #profile = inject(ProfileService);
  readonly #wallet = inject(WalletService);
  readonly #userPromocode = inject(UserPromocodeService);
  readonly #translate = inject(TranslateService);
  readonly #paths = inject(CABINET_PATHS);

  readonly hasAddresses = input(true);

  protected readonly readiness = computed(() => {
    const info = this.#profile.userInfo();
    const walletSummary = this.#wallet.summary();

    return resolveCabinetProfilePrerequisites({
      individual: info?.individual,
      hasEmail: !!info?.user?.email?.trim(),
      hasAddresses: this.hasAddresses(),
      hasPromocode: !!this.#userPromocode.getCode(),
      canCreateOrders: this.#wallet.canCreateOrders(),
      walletLoaded: walletSummary != null,
    });
  });

  protected readonly show = computed(
    () => !this.readiness().canPlaceOrder,
  );

  protected readonly messageKey = computed(() =>
    this.readiness().blockingPendingCount === this.readiness().blockingTotalCount
      ? 'CLIENT.CABINET.HOME.PROFILE_INCOMPLETE'
      : 'CLIENT.CABINET.HOME.PROFILE_INCOMPLETE_PARTIAL',
  );

  protected readonly messageParams = computed(() => {
    const params = resolveIncompleteProfileBannerParams(this.readiness());
    const steps = params.steps
      .split(',')
      .filter(Boolean)
      .map((kind) =>
        this.#translate.instant(
          resolvePrerequisiteLabelKey(
            kind as 'name' | 'fullProfile' | 'address' | 'wallet',
          ),
        ),
      )
      .join(', ');

    return {
      count: params.count,
      total: params.total,
      steps,
    };
  });

  protected readonly editLink = computed(
    () => `${this.#paths.client}/profile/edit`,
  );
}
